import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Box,
  Stack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha,
  useTheme,
  Fade,
} from '@mui/material';
import dayjs from 'dayjs';

import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SalaryListTable from '../../components/Table/SalaryListTable';
import HrReportV2 from './HrReportV2';
import HrSalaryDetail from './HrSalaryDetail';
import * as actions from '../../redux/actions';
import { openWindow, closeWindow } from '../../utils/functions/windowManager';

export default function HrSalaryList({ title, subtitle }) {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const salaryList = useSelector((state) => state.salaryList);
  // แถวที่กด "พิมพ์" (1-คลิก จากตาราง) — mount HrReportV2 แบบซ่อนไว้นอกจอ (ไม่โผล่ modal ให้เห็น)
  // ให้โหลดข้อมูล + คำนวณ empSummary/projectDashboard เหมือนเปิดจริง แล้วเรียก handlePrintFull()
  // (hrReportFullPDF) ให้เองอัตโนมัติ พอพิมพ์เสร็จก็ unmount ทิ้ง — ได้ PDF ฉบับเต็มแบบ 1 คลิก
  const [printingRow, setPrintingRow] = useState(null);

  useEffect(() => {
    dispatch(actions.salaryListAll({}));
    return () => {};
  }, []);

  const handleDelete = async (id) => {
    const confirm = window.confirm('คุณต้องการลบข้อมูลนี้หรือไม่?');
    if (confirm) {
      try {
        await dispatch(actions.salaryListDelete(id));
        
        // Update local state แทนการ reload
        const updatedRows = salaryList.rows.filter(row => row._id !== id);
        dispatch({
          type: 'SALARYLIST_UPDATE_LOCAL',
          payload: { rows: updatedRows, total: salaryList.total - 1 }
        });
      } catch (error) {
        console.error('Error deleting salary list:', error);
      }
    }
  };

  // เปิดผ่าน windowManager.js (global, mount ที่ root ใน index.js) แทน local state — ให้หน้าต่าง
  // ยังอยู่ใน taskbar ต่อได้แม้ user จะลิงก์ไปหน้าอื่นจนหน้านี้ (HrSalaryList) ถูก unmount ไปแล้ว
  // เหมือนพฤติกรรม minimize ของ Windows OS ที่สลับไปทำหน้าต่างอื่นได้โดยแอปที่ย่อไว้ไม่หายไปไหน
  const handleDetail = (id) => {
    const winId = `detail-${id}`;
    openWindow({
      id: winId,
      title: 'รายละเอียดเงินเดือน',
      defaultWidth: 1200,
      defaultHeight: 800,
      onClose: () => closeWindow(winId),
      render: () => (
        <div style={{ padding: '12px 20px', overflowY: 'auto', flex: 1 }}>
          <HrSalaryDetail
            id={id}
            onDeleted={() => {
              closeWindow(winId);
              dispatch(actions.salaryListAll({}));
            }}
          />
        </div>
      ),
    });
  };

  // FULLTIME จ่ายทั้งเดือนเสมอ (selectedOption='Month') ส่วน DAILY แบ่งเป็น 1-15 / 16-สิ้นเดือน
  // ดูจากวันที่เริ่มงวด (dateStart) — ตรงกับ logic แบ่งงวดใน HrReportV2.js (computeRange/renderCompactHeader)
  // ห้ามใช้ dateEnd เพราะเก็บเป็น 23:59:59.999Z (UTC) พอแปลงเป็น local time ไทย (+7) จะข้ามเที่ยงคืน
  // กลายเป็นวันถัดไป ทำให้เช็ค <=15 ผิดงวด (เจอบั๊กเดียวกันใน SalaryListTable.js formatPayPeriodLabel)
  const handleOpenReport = (row) => {
    const initial = {
      month: dayjs(row.dateStart).month(),
      year: dayjs(row.dateStart).year(),
      typeSelect: row.role_type || 'DAILY',
      selectedOption:
        row.role_type === 'FULLTIME'
          ? 'Month'
          : dayjs(row.dateStart).date() <= 15 ? '15' : '31',
    };
    const winId = `report-${row._id}`;
    openWindow({
      id: winId,
      title: 'ตรวจสอบเวลาเข้างาน',
      subtitle: `${dayjs(row.dateStart).format('DD/MM/BBBB')} - ${dayjs(row.dateEnd).format('DD/MM/BBBB')}`,
      taskbarLabel: `ตรวจสอบเวลา ${dayjs(row.dateStart).format('DD/MM/BB')}`,
      defaultWidth: 1400,
      defaultHeight: 860,
      onClose: () => closeWindow(winId),
      render: () => (
        <div style={{ padding: '12px 20px', overflowY: 'auto', flex: 1 }}>
          <HrReportV2
            initialMonth={initial.month}
            initialYear={initial.year}
            initialTypeSelect={initial.typeSelect}
            initialSelectedOption={initial.selectedOption}
            lockPeriod
          />
        </div>
      ),
    });
  };

  const handleQuickPrint = (row) => {
    setPrintingRow(row);
  };

  // instance ที่ mount ไว้พิมพ์แบบซ่อน (ไม่แสดงผลอะไรให้เห็นเลย) — เมื่อ printingRow ถูกตั้งค่า
  const renderHeadlessPrint = () => {
    if (!printingRow) return null;
    const initial = {
      month: dayjs(printingRow.dateStart).month(),
      year: dayjs(printingRow.dateStart).year(),
      typeSelect: printingRow.role_type || 'DAILY',
      selectedOption:
        printingRow.role_type === 'FULLTIME'
          ? 'Month'
          : dayjs(printingRow.dateStart).date() <= 15 ? '15' : '31',
    };
    return (
      <div style={{ position: 'fixed', top: -9999, left: -9999, width: 1400, height: 900, overflow: 'hidden', pointerEvents: 'none' }}>
        <HrReportV2
          key={printingRow._id}
          initialMonth={initial.month}
          initialYear={initial.year}
          initialTypeSelect={initial.typeSelect}
          initialSelectedOption={initial.selectedOption}
          lockPeriod
          autoPrintOnReady
          onAutoPrintDone={() => setPrintingRow(null)}
        />
      </div>
    );
  };

  const renderSkeletonTable = () => (
    <Fade in={true} timeout={300}>
      <Card elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                <TableCell><Skeleton variant="text" width={60} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={120} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={100} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={100} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={80} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={100} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={120} animation="wave" /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((row) => (
                <TableRow key={row}>
                  <TableCell><Skeleton variant="text" width={40} animation="wave" /></TableCell>
                  <TableCell>
                    <Box>
                      <Skeleton variant="text" width={150} animation="wave" />
                      <Skeleton variant="text" width={100} height={12} animation="wave" />
                    </Box>
                  </TableCell>
                  <TableCell><Skeleton variant="text" width={80} animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" width={60} animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" width={90} animation="wave" /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Skeleton variant="rounded" width={70} height={32} animation="wave" />
                      <Skeleton variant="rounded" width={70} height={32} animation="wave" />
                      <Skeleton variant="rounded" width={32} height={32} animation="wave" />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Fade>
  );

  const renderButtonAdd = () => (
    <div className="flex justify-end mb-4">
      <Button
        variant="contained"
        color="primary"
        onClick={() => history.push('/humen/salarylist/add')}
      >
        เพิ่ม
      </Button>
    </div>
  );
  const renderTableSalaryList = () => {
    if (salaryList?.isLoading) {
      return renderSkeletonTable();
    }
    
    return (
      <SalaryListTable
        salary={salaryList?.rows}
        handleDelete={handleDelete}
        handleDetail={handleDetail}
        onPrint={handleQuickPrint}
        handleOpenReport={handleOpenReport}
      />
    );
  };

  return (
      <div>
      {/* <div className="mb-4 bg-red-400 text-white text-center">
        โมดูลกำลังพัฒนา
      </div> */}

      {renderButtonAdd()}
      {renderTableSalaryList()}
      {renderHeadlessPrint()}
    </div>
  );
}
